/**
 * Weight Scale Parser — pure functions, no I/O.
 *
 * RS-232 weighing scales / POS scale boards emit a continuous ASCII stream,
 * typically 9600 baud, 8-N-1. Frames look like (varies by board):
 *   "ST,GS,+ 0.123kg\r\n"   (status, gross/net, sign, value, unit)
 *   "US,GS,+ 0.000kg\r\n"   (US = unstable)
 *   "+0.123 kg\r"
 *   "0.123"
 *
 * We keep one tolerant generic parser plus named presets so the right one can
 * be locked per device using the widget's raw-stream debug view. All weights
 * are normalised to KILOGRAMS.
 */

const r3 = (n) => Math.round((Number(n) || 0) * 1000) / 1000;

/**
 * Parse a single frame/line into { weightKg, unit, stable } or null if no
 * number is present. `stable` is true/false when the frame declares ST/US,
 * otherwise null (the hook then decides stability from reading steadiness).
 */
export const parseWeightFrame = (raw) => {
  if (raw == null) return null;
  const s = String(raw).trim();
  if (!s) return null;

  // Stability flag, when the scale sends one.
  let stable = null;
  if (/(^|[^A-Za-z])ST([^A-Za-z]|$)/i.test(s) || /stable/i.test(s)) stable = true;
  else if (/(^|[^A-Za-z])US([^A-Za-z]|$)/i.test(s) || /unstab/i.test(s)) stable = false;

  // Unit: grams only if "g" present without "kg".
  const hasKg = /kg/i.test(s);
  const isGram = !hasKg && /(^|[^A-Za-z])g([^A-Za-z]|$)/i.test(s);

  // First signed decimal number in the frame.
  const m = s.match(/[-+]?\d+(?:[.,]\d+)?/);
  if (!m) return null;
  let value = parseFloat(m[0].replace(',', '.'));
  if (Number.isNaN(value)) return null;
  if (isGram) value = value / 1000;

  return { weightKg: r3(value), unit: isGram ? 'g' : 'kg', stable };
};

/**
 * Protocol presets. v1 ships a single tolerant "generic" parser; the structure
 * lets us add board-specific presets later without touching the hook/widget.
 */
export const SCALE_PROTOCOLS = {
  generic: { label: 'Generic (auto-detect)', parse: parseWeightFrame },
};

export const getProtocol = (key) => SCALE_PROTOCOLS[key] || SCALE_PROTOCOLS.generic;

/**
 * Split a rolling text buffer into complete lines on CR / LF, returning
 * { lines, rest } where `rest` is the trailing incomplete fragment to keep.
 */
export const splitFrames = (buffer) => {
  const parts = buffer.split(/[\r\n]+/);
  const rest = parts.pop() ?? '';
  return { lines: parts.filter(p => p.trim() !== ''), rest };
};

const weightScaleParser = { parseWeightFrame, SCALE_PROTOCOLS, getProtocol, splitFrames };
export default weightScaleParser;
